// Service d'icônes basé sur lucide-vue-next.
//
// ⚠️ JAMAIS de `import * as LucideIcons from 'lucide-vue-next'` ici. Un import
// de namespace embarque les 1 637 icônes du catalogue, soit 837 Ko bruts qui
// atterrissent dans le chunk `vue-vendor` (capturé par le manualChunks de
// vite.config.ts, qui teste `id.includes('vue')` et attrape donc
// « lucide-vue-next »), lui-même préchargé au démarrage. Le découpage
// ci-dessous garde un rendu immédiat pour ce que l'app affiche vraiment, et ne
// va chercher le reste qu'à la demande :
//   - STATIC_ICONS : tout ce qui est atteignable SANS recherche (groupes
//     curatés du sélecteur, icônes écrites en dur dans les templates, table
//     emoji vers icône, valeurs par défaut). Imports nommés : élagués.
//   - loadFullIconCatalog() : le catalogue complet, en import dynamique, tiré
//     seulement si on déplie « toutes les icônes », qu'on lance une recherche,
//     ou qu'une tâche porte une icône choisie hors sélection curatée.
//
// Ajouter un slug à un groupe curaté sans l'ajouter à l'import ci-dessous
// n'est pas une régression : l'icône passera par le catalogue paresseux et
// s'affichera avec une frame de retard.
import { shallowRef } from 'vue';
import {
  Activity, AlarmClock, AlertTriangle, Apple, Armchair, ArrowLeft,
  ArrowRight, Award, Backpack, Badge, BadgeEuro, Banknote, BarChart3, Bath,
  Bed, Bell, Bike, Bird, Blinds, Bone, Book, BookOpen, Boxes,
  BrushCleaning, Bubbles, Building, Bus, Cake, Calculator, Calendar,
  CalendarCheck, CalendarDays, CalendarX, Camera, Candy, Car, Carrot, Cat,
  Check, CheckCircle, CheckCircle2, ChefHat, ChevronDown, ChevronLeft,
  ChevronRight, ChevronUp, ChevronsDown, Circle, CircleAlert, CircleCheck,
  Clapperboard, ClipboardList, Clock, Cloud, CloudOff, Coins, Cookie,
  CookingPot, Copy, CreditCard, Croissant, Crown, CupSoda, Diamond, Dices,
  Dog, Donut, DoorOpen, Drama, Droplet, Dumbbell, Ellipsis, Eye, EyeOff,
  FerrisWheel, FileText, Flame, Flower, Footprints, Gamepad, Gamepad2, Gem,
  Gift, Globe, GraduationCap, Guitar, Hammer, Hand, HandCoins, HandPlatter,
  Handshake, Headphones, Heart, HelpCircle, History, Home, Hourglass,
  House, IceCream, IceCreamBowl, IceCreamCone, Icon, Image, Info, Joystick,
  Key, Lamp, Languages, Layers, LayoutGrid, LayoutTemplate, Leaf, LifeBuoy,
  Lightbulb, ListChecks, ListTodo, Lock, LockOpen, LogOut, Lollipop, Mail,
  Mailbox, Medal, MessageCircle, MessageCircleQuestion, Minus, MonitorPlay,
  Moon, MoreHorizontal, Mountain, Music, Notebook, NotebookPen, Paintbrush,
  Palette, PartyPopper, PawPrint, Pencil, Phone, PhoneOff, PiggyBank, Pill,
  Pizza, Plane, Plug, Plus, PlusIcon, Popcorn, Power, Puzzle, QrCode,
  Rabbit, Receipt, Recycle, RefreshCw, Refrigerator, Repeat, Ribbon,
  Rocket, RollerCoaster, Ruler, Scissors, Search, Settings, Shapes, Share2,
  ShieldAlert, ShieldCheck, Ship, Shirt, ShoppingBag, ShoppingBasket,
  ShoppingCart, ShowerHead, SlidersHorizontal, Smartphone, Smile,
  SoapDispenserDroplet, Sofa, Sparkles, SprayCan, Sprout, SquareCheck,
  Star, Stethoscope, Sticker, Store, Sun, Sunrise, Sunset, Tablet,
  TabletSmartphone, Target, Tent, Theater, Thermometer, ThumbsDown,
  ThumbsUp, Ticket, Timer, Toilet, ToyBrick, Train, Trash, Trash2,
  TreePalm, TreePine, Trees, TriangleAlert, Trophy, Tv, Umbrella, User,
  UserCheck, UserPen, UserPlus, Users, Utensils, UtensilsCrossed, Video,
  Volleyball, Volume2, Wallet, WashingMachine, Waves, Wind, Wrench, X,
  XCircle,
} from 'lucide-vue-next';
import { getLucideIconFromEmoji } from '@/data/task-reward-templates';

type IconComponent = unknown;

/**
 * Icônes embarquées d'office, indexées en PascalCase : c'est la casse que
 * reçoit getIconComponent, les appelants passant par kebabToPascalCase.
 */
const STATIC_ICONS: Record<string, IconComponent> = {
  Activity, AlarmClock, AlertTriangle, Apple, Armchair, ArrowLeft,
  ArrowRight, Award, Backpack, Badge, BadgeEuro, Banknote, BarChart3, Bath,
  Bed, Bell, Bike, Bird, Blinds, Bone, Book, BookOpen, Boxes,
  BrushCleaning, Bubbles, Building, Bus, Cake, Calculator, Calendar,
  CalendarCheck, CalendarDays, CalendarX, Camera, Candy, Car, Carrot, Cat,
  Check, CheckCircle, CheckCircle2, ChefHat, ChevronDown, ChevronLeft,
  ChevronRight, ChevronUp, ChevronsDown, Circle, CircleAlert, CircleCheck,
  Clapperboard, ClipboardList, Clock, Cloud, CloudOff, Coins, Cookie,
  CookingPot, Copy, CreditCard, Croissant, Crown, CupSoda, Diamond, Dices,
  Dog, Donut, DoorOpen, Drama, Droplet, Dumbbell, Ellipsis, Eye, EyeOff,
  FerrisWheel, FileText, Flame, Flower, Footprints, Gamepad, Gamepad2, Gem,
  Gift, Globe, GraduationCap, Guitar, Hammer, Hand, HandCoins, HandPlatter,
  Handshake, Headphones, Heart, HelpCircle, History, Home, Hourglass,
  House, IceCream, IceCreamBowl, IceCreamCone, Icon, Image, Info, Joystick,
  Key, Lamp, Languages, Layers, LayoutGrid, LayoutTemplate, Leaf, LifeBuoy,
  Lightbulb, ListChecks, ListTodo, Lock, LockOpen, LogOut, Lollipop, Mail,
  Mailbox, Medal, MessageCircle, MessageCircleQuestion, Minus, MonitorPlay,
  Moon, MoreHorizontal, Mountain, Music, Notebook, NotebookPen, Paintbrush,
  Palette, PartyPopper, PawPrint, Pencil, Phone, PhoneOff, PiggyBank, Pill,
  Pizza, Plane, Plug, Plus, PlusIcon, Popcorn, Power, Puzzle, QrCode,
  Rabbit, Receipt, Recycle, RefreshCw, Refrigerator, Repeat, Ribbon,
  Rocket, RollerCoaster, Ruler, Scissors, Search, Settings, Shapes, Share2,
  ShieldAlert, ShieldCheck, Ship, Shirt, ShoppingBag, ShoppingBasket,
  ShoppingCart, ShowerHead, SlidersHorizontal, Smartphone, Smile,
  SoapDispenserDroplet, Sofa, Sparkles, SprayCan, Sprout, SquareCheck,
  Star, Stethoscope, Sticker, Store, Sun, Sunrise, Sunset, Tablet,
  TabletSmartphone, Target, Tent, Theater, Thermometer, ThumbsDown,
  ThumbsUp, Ticket, Timer, Toilet, ToyBrick, Train, Trash, Trash2,
  TreePalm, TreePine, Trees, TriangleAlert, Trophy, Tv, Umbrella, User,
  UserCheck, UserPen, UserPlus, Users, Utensils, UtensilsCrossed, Video,
  Volleyball, Volume2, Wallet, WashingMachine, Waves, Wind, Wrench, X,
  XCircle,
};

/**
 * Catalogue complet, chargé au plus une fois.
 *
 * `shallowRef` et non une simple variable : getIconComponent est appelé depuis
 * des `computed`, qui doivent se réévaluer quand le catalogue arrive. Sans
 * dépendance réactive, l'icône resterait vide jusqu'au prochain rendu
 * déclenché par autre chose.
 */
const fullCatalog = shallowRef<Record<string, IconComponent> | null>(null);
let catalogRequest: Promise<void> | null = null;

/** Tire le catalogue complet, une seule fois quelle que soit la concurrence. */
export function loadFullIconCatalog(): Promise<void> {
  if (!catalogRequest) {
    catalogRequest = import('lucide-vue-next')
      .then((mod) => {
        fullCatalog.value = mod as unknown as Record<string, IconComponent>;
      })
      .catch((error) => {
        // Chunk injoignable : on relâche la demande pour qu'un appel suivant
        // puisse réessayer, plutôt que de figer l'app sur un catalogue vide.
        catalogRequest = null;
        console.warn('[icons] catalogue Lucide complet indisponible:', error);
      });
  }
  return catalogRequest;
}

/** Composant Lucide pour un nom d'icône, ou null si rien ne correspond. */
export const getIconComponent = (iconName: string | null | undefined) => {
  if (!iconName) return null;

  const capitalizedName = iconName.charAt(0).toUpperCase() + iconName.slice(1);

  const fromStatic = STATIC_ICONS[iconName] ?? STATIC_ICONS[capitalizedName];
  if (fromStatic) return fromStatic;

  // Hors sélection embarquée : le catalogue complet, s'il est déjà arrivé.
  // Lire `.value` inscrit la dépendance réactive, donc le computed appelant se
  // réévalue de lui-même dès que le chunk est là.
  const catalog = fullCatalog.value;
  if (catalog) {
    const fromCatalog = catalog[capitalizedName];
    return typeof fromCatalog === 'function' ? fromCatalog : null;
  }

  void loadFullIconCatalog();
  return null;
};


/**
 * Groupe d'icônes curatées affiché en tête du sélecteur.
 * `id` sert de clé i18n (`tasks.form.iconGroups.<id>`) ET d'icône Lucide pour
 * le titre du groupe : un seul identifiant à maintenir.
 */
export interface IconGroup {
  id: string;
  /** Icône Lucide du titre de groupe. */
  headerIcon: string;
  /** Slugs kebab-case Lucide, 10 par groupe (2 rangées de 5). */
  icons: string[];
}

// Sélection curatée d'icônes pour les TÂCHES : gestes du quotidien familial
// (ménage, hygiène, école, animaux, plantes) plutôt qu'icônes génériques
// d'interface (Target/Zap/Settings/Cog n'ont pas de sens pour une corvée).
// Format : slugs kebab-case Lucide (ex. 'trash-2'), comme LUCIDE_ICON_NAMES.
// Groupé plutôt qu'en bloc unique : à 70 icônes, une grille plate redevient le
// mur qu'on cherchait à éviter. Les groupes suivent le déroulé d'une journée de
// famille, pas l'alphabet — 10 par groupe pour deux rangées pleines de 5.
export const getTaskIconGroups = (): IconGroup[] => [
  {
    id: 'home',
    headerIcon: 'house',
    // Pas de 'blinds' (volet) : illisible à 24px, il se lit comme une liste.
    icons: ['house', 'bed', 'sofa', 'armchair', 'lamp', 'door-open', 'mailbox', 'key', 'hammer', 'plug'],
  },
  {
    id: 'cleaning',
    headerIcon: 'brush-cleaning',
    icons: ['brush-cleaning', 'sparkles', 'trash-2', 'recycle', 'washing-machine', 'shirt', 'spray-can', 'bubbles', 'boxes', 'wind'],
  },
  {
    id: 'kitchen',
    headerIcon: 'utensils',
    icons: ['utensils', 'utensils-crossed', 'chef-hat', 'cooking-pot', 'refrigerator', 'hand-platter', 'shopping-cart', 'shopping-basket', 'apple', 'carrot'],
  },
  {
    id: 'care',
    headerIcon: 'shower-head',
    icons: ['shower-head', 'bath', 'toilet', 'soap-dispenser-droplet', 'hand', 'smile', 'scissors', 'pill', 'stethoscope', 'thermometer'],
  },
  {
    id: 'school',
    headerIcon: 'graduation-cap',
    icons: ['book-open', 'graduation-cap', 'backpack', 'pencil', 'notebook-pen', 'calculator', 'ruler', 'languages', 'palette', 'music'],
  },
  {
    id: 'outdoors',
    headerIcon: 'paw-print',
    icons: ['dog', 'cat', 'paw-print', 'bone', 'bird', 'sprout', 'flower', 'trees', 'leaf', 'bike'],
  },
  {
    id: 'routine',
    headerIcon: 'clock',
    icons: ['clock', 'alarm-clock', 'sunrise', 'sunset', 'moon', 'sun', 'calendar-days', 'calendar-check', 'bell', 'hourglass'],
  },
];

// Sélection curatée d'icônes pour les RÉCOMPENSES : univers famille avec
// enfants de 5 à 14 ans — délibérément SANS alcool/café (Wine/Beer/Coffee de
// l'ancienne liste), hors-sujet pour une récompense d'enfant.
export const getRewardIconGroups = (): IconGroup[] => [
  {
    id: 'trophies',
    headerIcon: 'trophy',
    icons: ['trophy', 'award', 'medal', 'star', 'crown', 'gem', 'sparkles', 'party-popper', 'rocket', 'heart'],
  },
  {
    id: 'treats',
    headerIcon: 'ice-cream-cone',
    icons: ['ice-cream-cone', 'cookie', 'cake', 'candy', 'lollipop', 'donut', 'pizza', 'popcorn', 'croissant', 'cup-soda'],
  },
  {
    id: 'screens',
    headerIcon: 'gamepad-2',
    icons: ['gamepad-2', 'tv', 'smartphone', 'tablet', 'joystick', 'headphones', 'monitor-play', 'clapperboard', 'dices', 'puzzle'],
  },
  {
    id: 'outings',
    headerIcon: 'ticket',
    icons: ['ticket', 'ferris-wheel', 'tent', 'plane', 'tree-palm', 'mountain', 'waves', 'roller-coaster', 'theater', 'volleyball'],
  },
  {
    id: 'creative',
    headerIcon: 'palette',
    icons: ['palette', 'paintbrush', 'pencil', 'camera', 'music', 'guitar', 'book-open', 'drama', 'sticker', 'toy-brick'],
  },
  {
    id: 'money',
    headerIcon: 'piggy-bank',
    icons: ['coins', 'piggy-bank', 'wallet', 'banknote', 'hand-coins', 'gift', 'shopping-bag', 'store', 'badge-euro', 'receipt'],
  },
];

/** Aplatit des groupes en une liste dédupliquée (l'ordre des groupes fait foi). */
const flattenGroups = (groups: IconGroup[]): string[] =>
  [...new Set(groups.flatMap(group => group.icons))];

export const getPopularTaskIcons = (): string[] => flattenGroups(getTaskIconGroups());

export const getPopularRewardIcons = (): string[] => flattenGroups(getRewardIconGroups());

// ─────────────────────────────────────────────────────────────────────────
// Recherche d'icônes par mot-clé (FR + EN)
// ─────────────────────────────────────────────────────────────────────────
// Les noms d'icônes Lucide sont des mots anglais (ex. 'utensils'), donc une
// simple recherche par sous-chaîne échoue pour tout terme français ("vaisselle"
// ne matche jamais "utensils") et pour beaucoup de synonymes anglais courants
// ("dishes", "homework", "money"...). Cet index comble l'écart pour le
// vocabulaire du quotidien familial (corvées + récompenses) sans viser
// l'exhaustivité — le filtrage par sous-chaîne reste le filet de sécurité
// pour tout le reste.

/** Normalise un terme de recherche : minuscule + accents retirés (NFD). */
export function normalizeIconSearchTerm(input: string): string {
  return input
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

// Clé = terme normalisé (normalizeIconSearchTerm), valeur = slugs kebab-case
// Lucide, du plus au moins pertinent. Un slug peut être ciblé par plusieurs
// synonymes (FR + EN) et apparaître dans plusieurs entrées.
const ICON_KEYWORD_SYNONYMS: Record<string, string[]> = {
  // Ménage / maison
  menage: ['sparkles', 'brush-cleaning'],
  nettoyage: ['sparkles', 'brush-cleaning'],
  nettoyer: ['sparkles', 'brush-cleaning'],
  vaisselle: ['utensils', 'utensils-crossed'],
  dishes: ['utensils', 'utensils-crossed'],
  poubelle: ['trash-2'],
  dechet: ['trash-2'],
  dechets: ['trash-2'],
  garbage: ['trash-2'],
  linge: ['shirt', 'washing-machine'],
  lessive: ['washing-machine'],
  laundry: ['shirt', 'washing-machine'],
  chambre: ['bed', 'house'],
  bedroom: ['bed', 'house'],
  douche: ['shower-head'],
  shower: ['shower-head'],
  bain: ['bath'],
  dents: ['smile', 'brush-cleaning'],
  teeth: ['smile', 'brush-cleaning'],
  brosser: ['brush-cleaning'],
  brossage: ['brush-cleaning'],
  ranger: ['boxes', 'house'],
  rangement: ['boxes'],
  tidy: ['boxes'],
  cuisine: ['chef-hat', 'cooking-pot'],
  kitchen: ['chef-hat', 'cooking-pot'],
  cuisiner: ['chef-hat', 'cooking-pot'],
  canape: ['sofa'],
  couch: ['sofa'],
  courses: ['shopping-cart'],
  groceries: ['shopping-cart'],
  animal: ['dog', 'cat', 'paw-print'],
  animaux: ['dog', 'cat', 'paw-print'],
  pet: ['dog', 'cat', 'paw-print'],
  chien: ['dog'],
  chat: ['cat'],
  plante: ['sprout'],
  plantes: ['sprout'],
  jardin: ['sprout'],
  garden: ['sprout'],
  arroser: ['sprout'],
  watering: ['sprout'],
  // École / apprentissage
  devoirs: ['book-open', 'pencil'],
  homework: ['book-open', 'pencil'],
  ecole: ['graduation-cap', 'backpack'],
  school: ['graduation-cap', 'backpack'],
  cartable: ['backpack'],
  lecture: ['book-open'],
  reading: ['book-open'],
  livre: ['book-open'],
  velo: ['bike'],
  bicycle: ['bike'],
  reveil: ['alarm-clock'],
  heure: ['clock'],
  horaire: ['clock'],
  // Argent de poche / récompenses
  argent: ['coins', 'piggy-bank', 'wallet'],
  money: ['coins', 'piggy-bank', 'wallet'],
  poche: ['piggy-bank'],
  tirelire: ['piggy-bank'],
  cadeau: ['gift'],
  present: ['gift'],
  bonbon: ['candy', 'lollipop'],
  candy: ['candy', 'lollipop'],
  sucrerie: ['candy'],
  glace: ['ice-cream-cone', 'ice-cream-bowl'],
  film: ['clapperboard', 'ticket'],
  movie: ['clapperboard', 'ticket'],
  cinema: ['clapperboard', 'ticket'],
  sortie: ['ticket', 'ferris-wheel'],
  outing: ['ticket', 'ferris-wheel'],
  ecran: ['smartphone', 'tv', 'tablet'],
  screen: ['smartphone', 'tv', 'tablet'],
  tablette: ['tablet'],
  telephone: ['smartphone'],
  fete: ['party-popper', 'cake'],
  party: ['party-popper', 'cake'],
  trophee: ['trophy'],
  recompense: ['trophy', 'gift'],
  reward: ['trophy', 'gift'],
  dessin: ['palette'],
  peinture: ['palette'],
  drawing: ['palette'],
  musique: ['headphones'],
  music: ['headphones'],
  ecouteurs: ['headphones'],
  jeu: ['gamepad-2', 'dices', 'puzzle', 'toy-brick'],
  jeux: ['gamepad-2', 'dices', 'puzzle', 'toy-brick'],
  jouer: ['gamepad-2', 'joystick'],
  // Maison / objets (groupes curatés « Maison » et « Ménage »)
  meuble: ['sofa', 'armchair'],
  fauteuil: ['armchair'],
  lampe: ['lamp'],
  lumiere: ['lamp'],
  light: ['lamp'],
  porte: ['door-open'],
  door: ['door-open'],
  volet: ['blinds'],
  rideau: ['blinds'],
  courrier: ['mailbox'],
  mail: ['mailbox'],
  // Pas de clé « cle » : la recherche matche aussi par inclusion inverse, et
  // "clean" contient "cle" — l'icône clé remonterait en tête d'une recherche
  // de ménage. Le slug 'key' reste trouvable par sous-chaîne.
  clef: ['key'],
  serrure: ['key'],
  bricolage: ['hammer', 'wrench'],
  reparer: ['hammer', 'wrench'],
  repair: ['hammer', 'wrench'],
  marteau: ['hammer'],
  prise: ['plug'],
  electricite: ['plug'],
  recyclage: ['recycle'],
  recycling: ['recycle'],
  aspirateur: ['spray-can', 'brush-cleaning'],
  produit: ['spray-can'],
  spray: ['spray-can'],
  savon: ['bubbles', 'soap-dispenser-droplet'],
  soap: ['bubbles', 'soap-dispenser-droplet'],
  bulle: ['bubbles'],
  aerer: ['wind'],
  vent: ['wind'],
  fenetre: ['wind', 'blinds'],
  // Cuisine / repas
  chef: ['chef-hat'],
  casserole: ['cooking-pot'],
  marmite: ['cooking-pot'],
  frigo: ['refrigerator'],
  refrigerateur: ['refrigerator'],
  fridge: ['refrigerator'],
  servir: ['hand-platter'],
  debarrasser: ['hand-platter'],
  panier: ['shopping-basket'],
  basket: ['shopping-basket'],
  fruit: ['apple', 'carrot'],
  pomme: ['apple'],
  legume: ['carrot'],
  carotte: ['carrot'],
  gouter: ['croissant', 'cookie'],
  snack: ['croissant', 'cookie'],
  boisson: ['cup-soda'],
  drink: ['cup-soda'],
  // Hygiène / soins
  toilettes: ['toilet'],
  toilet: ['toilet'],
  main: ['hand'],
  hands: ['hand'],
  ongles: ['scissors'],
  ciseaux: ['scissors'],
  cheveux: ['scissors'],
  medicament: ['pill'],
  medicine: ['pill'],
  sante: ['stethoscope', 'pill'],
  health: ['stethoscope', 'pill'],
  docteur: ['stethoscope'],
  doctor: ['stethoscope'],
  fievre: ['thermometer'],
  fever: ['thermometer'],
  // École / créatif
  cahier: ['notebook-pen'],
  notebook: ['notebook-pen'],
  calcul: ['calculator'],
  math: ['calculator'],
  maths: ['calculator'],
  regle: ['ruler'],
  langue: ['languages'],
  language: ['languages'],
  anglais: ['languages'],
  photo: ['camera'],
  camera: ['camera'],
  guitare: ['guitar'],
  guitar: ['guitar'],
  instrument: ['guitar', 'music'],
  theatre: ['drama', 'theater'],
  autocollant: ['sticker'],
  sticker: ['sticker'],
  lego: ['toy-brick'],
  brique: ['toy-brick'],
  pinceau: ['paintbrush'],
  // Animaux / nature
  oiseau: ['bird'],
  bird: ['bird'],
  fleur: ['flower'],
  flower: ['flower'],
  arbre: ['trees'],
  tree: ['trees'],
  feuille: ['leaf'],
  leaf: ['leaf'],
  // Rythme de la journée
  matin: ['sunrise'],
  morning: ['sunrise'],
  lever: ['sunrise', 'alarm-clock'],
  soir: ['sunset', 'moon'],
  evening: ['sunset', 'moon'],
  coucher: ['moon', 'bed'],
  bedtime: ['moon', 'bed'],
  nuit: ['moon'],
  night: ['moon'],
  soleil: ['sun'],
  agenda: ['calendar-days', 'calendar-check'],
  calendrier: ['calendar-days', 'calendar-check'],
  calendar: ['calendar-days', 'calendar-check'],
  rappel: ['bell', 'alarm-clock'],
  reminder: ['bell', 'alarm-clock'],
  cloche: ['bell'],
  sablier: ['hourglass'],
  chrono: ['hourglass'],
  timer: ['hourglass'],
  // Récompenses : trophées, sorties, achats
  couronne: ['crown'],
  crown: ['crown'],
  diamant: ['gem'],
  bijou: ['gem'],
  fusee: ['rocket'],
  rocket: ['rocket'],
  beignet: ['donut'],
  donut: ['donut'],
  manege: ['ferris-wheel', 'roller-coaster'],
  camping: ['tent'],
  tente: ['tent'],
  avion: ['plane'],
  voyage: ['plane', 'tree-palm'],
  travel: ['plane', 'tree-palm'],
  vacances: ['tree-palm', 'plane'],
  holiday: ['tree-palm', 'plane'],
  plage: ['tree-palm', 'waves'],
  beach: ['tree-palm', 'waves'],
  montagne: ['mountain'],
  mountain: ['mountain'],
  piscine: ['waves'],
  natation: ['waves'],
  swimming: ['waves'],
  sport: ['volleyball', 'bike'],
  ballon: ['volleyball'],
  billet: ['banknote', 'ticket'],
  achat: ['shopping-bag', 'store'],
  shopping: ['shopping-bag', 'store'],
  magasin: ['store'],
  shop: ['store'],
  euro: ['badge-euro', 'banknote'],
  recu: ['receipt'],
}

/**
 * Filtre `icons` (slugs kebab-case) sur `query` : sous-chaîne dans le slug
 * (comportement historique) UNIE aux correspondances de mots-clés FR/EN
 * (ICON_KEYWORD_SYNONYMS). Les correspondances par mot-clé sont placées en
 * tête (plus intentionnelles qu'un simple hasard de sous-chaîne), le reste
 * garde l'ordre de `icons` (déjà alphabétique).
 */
export function searchIconsByKeyword(query: string, icons: string[]): string[] {
  const normalized = normalizeIconSearchTerm(query)
  if (!normalized) return icons

  const iconSet = new Set(icons)
  const keywordMatches: string[] = []
  const seen = new Set<string>()
  for (const [keyword, slugs] of Object.entries(ICON_KEYWORD_SYNONYMS)) {
    if (keyword.includes(normalized) || normalized.includes(keyword)) {
      for (const slug of slugs) {
        if (iconSet.has(slug) && !seen.has(slug)) {
          seen.add(slug)
          keywordMatches.push(slug)
        }
      }
    }
  }

  const substringMatches = icons.filter(
    name => name.includes(normalized) && !seen.has(name),
  )

  return [...keywordMatches, ...substringMatches]
}

/**
 * Nom d'icône Lucide pour une récompense.
 * reward.icon est déjà un nom Lucide valide (IconPicker) ; on ne fait que le défaut si vide.
 */
export const getRewardLucideIconName = (icon: string | null | undefined): string => {
  return icon?.trim() || 'Gift';
};

/**
 * Nom d'icône Lucide pour une tâche.
 * task.icon contient soit un nom Lucide (IconPicker émet `lucide:x`, les modèles
 * un nom nu), soit un EMOJI hérité (création depuis un modèle sans champ icon :
 * `template.icon || template.emoji`). Les emojis sont résolus vers Lucide ; un
 * emoji inconnu ou une valeur vide retombe sur le défaut.
 */
export const getTaskLucideIconName = (icon: string | null | undefined): string => {
  const raw = icon?.trim();
  if (!raw) return 'sparkles';
  return getLucideIconFromEmoji(raw) || raw;
};